import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { config } from '../config';
import { USE_MFA_FLAG } from '../components/Auth/MfaConfig';

export interface MfaContext {
  isMfa: boolean;
  scope: string;
  setIsMfa: (isMfa: boolean) => void;
}

const context = createContext<MfaContext>({
  isMfa: false,
  scope: config.scope,
  setIsMfa: (isMfa: boolean) => {}
});

export const useMfa = () => useContext(context);

interface MfaProviderProps {
  children: ReactNode;
}

export const MFA_STORAGE_KEY = 'expMfaTimestamp';

export const MfaProvider = ({ children }: MfaProviderProps) => {
  const [isMfa, setIsMfa] = useState<boolean | undefined>(false);
  const [scope, setScope] = useState<string>(config.scope);

  // Init loading, checking url and session storage to get info about mfa scope
  useEffect(() => {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    const possibleMfaValue = searchParams.get(USE_MFA_FLAG);

    const expMfaString = window.sessionStorage.getItem(MFA_STORAGE_KEY);
    const expMfaTimestamp = expMfaString ? parseInt(expMfaString, 10) : 0;

    setIsMfa(possibleMfaValue === 'true' || new Date(expMfaTimestamp) > new Date());

    // removing not needed query param
    if (possibleMfaValue) {
      searchParams.delete(USE_MFA_FLAG);
      url.search = searchParams.toString();
      window.history.replaceState(null, '', url.toString());
    }
  }, []);

  useEffect(() => {
    setScope(config.scope + (isMfa ? ' mfa' : ''));
  }, [isMfa]);

  if (isMfa === undefined || (isMfa && !scope.includes('mfa'))) return <></>;

  return (
    <context.Provider
      value={{
        isMfa,
        scope,
        setIsMfa: (newMfa: boolean) => {
          setIsMfa(newMfa);
        }
      }}
    >
      {children}
    </context.Provider>
  );
};
